// Theme selection.
//
// Light is the default. The choice is persisted per browser and applied as a
// `data-theme` attribute on <html>, which is also set by an inline script in
// index.html before first paint so there is no flash of the wrong theme.
//
// Embeds can be pinned with `?theme=dark`, since the host page — not the
// visitor's Cloudish preference — decides what looks right in an iframe.

import { useCallback, useEffect, useState } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE = 'cloudish.theme';
export const DEFAULT_THEME: ThemePreference = 'light';

const listeners = new Set<(preference: ThemePreference) => void>();

function systemTheme(): ResolvedTheme {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? systemTheme() : preference;
}

export function readPreference(): ThemePreference {
  // A pinned embed theme wins over anything stored.
  const pinned = new URLSearchParams(window.location.search).get('theme');
  if (pinned === 'light' || pinned === 'dark') return pinned;

  const stored = localStorage.getItem(THEME_STORAGE);
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : DEFAULT_THEME;
}

export function applyTheme(preference: ThemePreference) {
  const resolved = resolveTheme(preference);
  document.documentElement.dataset.theme = resolved;
  // Lets the browser style form controls, scrollbars and caret to match.
  document.documentElement.style.colorScheme = resolved;
}

export function setPreference(preference: ThemePreference) {
  if (preference === DEFAULT_THEME) localStorage.removeItem(THEME_STORAGE);
  else localStorage.setItem(THEME_STORAGE, preference);
  applyTheme(preference);
  for (const listener of listeners) listener(preference);
}

/**
 * Subscribes a component to the current theme.
 * Returns the stored preference, what it resolves to, and setters.
 */
export function useTheme() {
  const [preference, setLocal] = useState<ThemePreference>(() => readPreference());

  useEffect(() => {
    const listener = (next: ThemePreference) => setLocal(next);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  // Follow the OS while the preference is "system".
  useEffect(() => {
    if (preference !== 'system') return undefined;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [preference]);

  const set = useCallback((next: ThemePreference) => setPreference(next), []);
  const toggle = useCallback(() => {
    setPreference(resolveTheme(readPreference()) === 'dark' ? 'light' : 'dark');
  }, []);

  return { preference, resolved: resolveTheme(preference), setTheme: set, toggle };
}

/** Applies the stored preference at startup. */
export function initTheme() {
  applyTheme(readPreference());
}
